/**
 * UpdatePrompt - Clara
 *
 * Prompt-to-refresh toast for the PWA update flow (issue #263). The service
 * worker is generated by vite-plugin-pwa with `registerType: 'prompt'`: a new
 * deploy installs in the background and waits — nothing changes under the
 * user mid-session — until they accept the refresh here (or close every tab).
 * Mounted once at the app root (main.tsx), same as OfflineBanner. Its copy
 * lives in the `common` catalog under `pwaUpdate` (issue #781).
 */
import { useTranslation } from 'react-i18next'
import { useRegisterSW } from 'virtual:pwa-register/react'

/**
 * One-time migration sweep: the hand-rolled sw.js this SW replaced kept its
 * own `reconflux-*` named caches, which Workbox's cleanupOutdatedCaches
 * doesn't know about. Safe to delete a few releases after #263 ships.
 */
function cleanupLegacyCaches() {
  if (!('caches' in window)) return
  void caches
    .keys()
    .then((keys) =>
      Promise.all(keys.filter((key) => key.startsWith('reconflux-')).map((key) => caches.delete(key))),
    )
    .catch(() => {})
}

export function UpdatePrompt() {
  const { t } = useTranslation('common')
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW() {
      cleanupLegacyCaches()
    },
  })

  if (!needRefresh) return null

  return (
    <div
      role="status"
      className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] inset-x-4 z-50 rounded-xl border border-edge bg-surface-page/95 p-4 shadow-lg shadow-black/40 backdrop-blur sm:inset-x-auto sm:right-4 sm:w-96 md:bottom-[calc(1rem+env(safe-area-inset-bottom,0px))]"
    >
      <p className="text-sm text-ink mb-3">
        {t('pwaUpdate.ready')}
      </p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setNeedRefresh(false)}
          className="min-h-11 px-4 rounded-lg text-sm font-medium text-ink-muted hover:text-ink"
        >
          {t('pwaUpdate.later')}
        </button>
        <button
          type="button"
          onClick={() => void updateServiceWorker(true)}
          className="min-h-11 px-4 rounded-lg text-sm font-semibold bg-accent text-accent-contrast hover:opacity-90"
        >
          {t('pwaUpdate.refresh')}
        </button>
      </div>
    </div>
  )
}
